import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createVNode as _createVNode, isRef as _isRef, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "col span-6" }
const _hoisted_4 = { class: "row" }
const _hoisted_5 = { class: "col span-11" }

import { useStore } from 'vuex';
import { ref, computed, onMounted, watch } from 'vue';

import EpinioConfiguration from '../models/configurations';
import { useEpinioBindAppsMixin } from './bind-apps-mixin';
import { epinioExceptionToErrorsArray } from '../utils/errors';
import { EPINIO_TYPES, EpinioNamespace } from '../types';

import { sortBy } from '@shell/utils/sort';
import { _EDIT } from '@shell/config/query-params';
import Loading from '@shell/components/Loading.vue';
import Banner from '@components/Banner/Banner.vue';
import KeyValue from '@shell/components/form/KeyValue.vue';
import CruResource from '@shell/components/CruResource.vue';
import LabeledSelect from '@shell/components/form/LabeledSelect.vue';
import NameNsDescription from '@shell/components/form/NameNsDescription.vue';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';


export default /*@__PURE__*/_defineComponent({
  __name: 'configurations',
  props: {
    value: {},
    initialValue: {},
    mode: {}
  },
  setup(__props: any) {

const store = useStore();
const t = store.getters['i18n/t'];

const props = __props;

const { 
  doneParams,
  doneRoute,
  selectedApps, 
  nsAppOptions, 
  noApps, 
  updateConfigurationAppBindings,
} = useEpinioBindAppsMixin(props);

const errors = ref<Array<any>>([]);
const pending = ref<boolean>(true);
const validationPassed = ref<boolean>(false);

onMounted(async () => {
  props.value.meta.namespace = props.initialValue.meta.namespace || 
    namespaces.value[0]?.metadata.name;
  props.value.data = { ...props.initialValue.configuration?.details };
  selectedApps.value = [...props.initialValue.configuration?.boundapps || []];

  updateValidation();

  pending.value = false;
});

const isEdit = computed(() => {
  return props.mode === _EDIT;
});

const namespaces = computed(() => {
  return sortBy(store.getters['epinio/all'](EPINIO_TYPES.NAMESPACE), 'name', false);
});

const namespaceNames = computed(() => {
  return namespaces.value.map((n: EpinioNamespace) => n.metadata.name);
});

const done = () => {
  if (!doneRoute) {
    return;
  }

  store.$router.replace({
    name:   doneRoute.value,
    params: doneParams.value || { resource: props.value.type },
  });
}

const save = async (saveCb: (success: boolean) => void) => {
  errors.value = [];

  try {
    if (!isEdit.value) {
      await props.value.create();
      await updateConfigurationAppBindings();
      await store.dispatch(
        'epinio/findAll', 
        { type: props.value.type, opt: { force: true } },
      );
    }

    if (isEdit.value) {
      await props.value.update();
      await updateConfigurationAppBindings();
      await props.value.forceFetch();
    }

    saveCb(true);
    done();
  } catch (err) {
    console.log(err);
    errors.value = epinioExceptionToErrorsArray(err);
    saveCb(false);
  }
};

const updateValidation = () => {
  const nameErrors = validateKubernetesName(
    props.value?.meta.name || '', 
    t('epinio.namespace.name'), 
    store.getters, 
    undefined, 
    [],
  );
  
  const nsErrors = validateKubernetesName(
    props.value?.meta.namespace || '', 
    '', 
    store.getters, 
    undefined, 
    [],
  );

  if (nameErrors.length === 0 && nsErrors.length === 0) {
    const dataValues = Object.entries(props.value?.data || {});
    
    if (!!dataValues.length) { // eslint-disable-line no-extra-boolean-cast
      validationPassed.value = true;
      return;
    }
  }

  validationPassed.value = false;
};

watch(
  () => props.value.meta.namespace,
  () => {
    selectedApps.value = [];
    updateValidation(); // For when a user is supplying their own ns
  }
);

watch(
  () => props.value.meta.name,
  () => {
    updateValidation();
  }
);

watch(
  () => props.value.data,
  () => {
    updateValidation();
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  return (!_ctx.value || pending.value)
    ? (_openBlock(), _createBlock(Loading, { key: 0 }))
    : (_ctx.value)
      ? (_openBlock(), _createBlock(CruResource, {
          key: 1,
          "min-height": '7em',
          mode: _ctx.mode,
          "done-route": _unref(doneRoute),
          resource: _ctx.value,
          "can-yaml": false,
          errors: errors.value,
          "validation-passed": validationPassed.value,
          "namespace-key": "meta.namespace",
          onError: _cache[2] || (_cache[2] = (e) => (errors.value = e)),
          onFinish: save
        }, {
          default: _withCtx(() => [
            (_ctx.value.isServiceRelated)
              ? (_openBlock(), _createBlock(Banner, {
                  key: 0,
                  color: "info"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('epinio.configurations.tableHeaders.service.tooltip')), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            _cache[3] || (_cache[3] = _createTextVNode()),
            (errors.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
                    return (_openBlock(), _createBlock(Banner, {
                      key: i,
                      color: "error",
                      label: err
                    }, null, 8, ["label"]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            _cache[4] || (_cache[4] = _createTextVNode()),
            _createVNode(NameNsDescription, {
              "name-key": "name",
              "namespace-key": "namespace",
              "namespaces-override": namespaceNames.value,
              "create-namespace-override": true,
              "description-hidden": true,
              value: _ctx.value.meta,
              mode: _ctx.mode
            }, null, 8, ["namespaces-override", "value", "mode"]),
            _cache[5] || (_cache[5] = _createTextVNode()),
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(LabeledSelect, {
                  value: _unref(selectedApps),
                  "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => (_isRef(selectedApps) ? (selectedApps).value = $event : null)),
                  loading: pending.value,
                  disabled: _unref(noApps),
                  options: _unref(nsAppOptions),
                  searchable: true,
                  mode: _ctx.mode,
                  multiple: true,
                  "label-key": 'epinio.configurations.bindApps.label',
                  placeholder: _unref(noApps) ? _unref(t)('epinio.configurations.bindApps.placeholderNoOptions') : _unref(t)('epinio.configurations.bindApps.placeholderWithOptions')
                }, null, 8, ["value", "loading", "disabled", "options", "mode", "placeholder"])
              ])
            ]),
            _cache[6] || (_cache[6] = _createTextVNode()),
            _cache[7] || (_cache[7] = _createElementVNode("div", { class: "spacer" }, null, -1)),
            _cache[8] || (_cache[8] = _createTextVNode()),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(KeyValue, {
                  value: _ctx.value.data,
                  "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((_ctx.value.data) = $event)),
                  "initial-empty-row": true,
                  mode: _ctx.mode,
                  title: _unref(t)('epinio.configurations.pairs.label'),
                  "title-protip": _unref(t)('epinio.configurations.pairs.tooltip'),
                  "key-label": _unref(t)('epinio.applications.create.envvar.keyLabel'),
                  "value-label": _unref(t)('epinio.applications.create.envvar.valueLabel'),
                  valueMarkdownMultiline: true,
                  "parse-lines-from-file": true,
                  "parse-value-from-file": true
                }, null, 8, ["value", "mode", "title", "title-protip", "key-label", "value-label"])
              ])
            ])
          ]),
          _: 1,
          __: [3,4,5,6,7,8]
        }, 8, ["mode", "done-route", "resource", "errors", "validation-passed"]))
      : _createCommentVNode("", true)
}
}

})